/*
  Lazuela additions to Impulse.

  A single controller for the product carousel used by the "Featured collection" section. The
  track is a plain horizontally scrolling element, so the section still works with JavaScript
  off and on touch devices - this file only adds the desktop affordances (arrows and dots) on
  top of that native scrolling.
*/
(function () {
  'use strict';

  var DESKTOP = '(min-width: 769px)';

  function LzCarousel(container) {
    this.container = container;
    this.viewport = container.querySelector('[data-lz-carousel-viewport]');
    this.prev = container.querySelector('[data-lz-carousel-prev]');
    this.next = container.querySelector('[data-lz-carousel-next]');
    this.dotsWrapper = container.querySelector('[data-lz-carousel-dots]');
    this.pageCount = 0;
    this.currentPage = 0;

    if (!this.viewport) return;

    this.onScroll = this.throttle(this.update.bind(this), 100);
    this.onResize = this.debounce(this.build.bind(this), 150);

    this.viewport.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onResize);

    if (this.prev) {
      this.prev.addEventListener('click', this.scrollByPage.bind(this, -1));
    }
    if (this.next) {
      this.next.addEventListener('click', this.scrollByPage.bind(this, 1));
    }

    /*
      Product images are lazy-loaded, so the track is narrower than its final width for the
      first moment. Rebuilding once everything has settled keeps the dot count honest.
    */
    if (window.ResizeObserver) {
      this.observer = new ResizeObserver(this.onResize);
      this.observer.observe(this.viewport);
      var track = this.viewport.firstElementChild;
      if (track) this.observer.observe(track);
    } else {
      window.addEventListener('load', this.onResize);
    }

    this.build();
  }

  LzCarousel.prototype.debounce = function (fn, wait) {
    var timer;
    return function () {
      clearTimeout(timer);
      timer = setTimeout(fn, wait);
    };
  };

  LzCarousel.prototype.throttle = function (fn, wait) {
    var waiting = false;
    return function () {
      if (waiting) return;
      waiting = true;
      window.setTimeout(function () {
        waiting = false;
        fn();
      }, wait);
    };
  };

  LzCarousel.prototype.isDesktop = function () {
    return window.matchMedia(DESKTOP).matches;
  };

  LzCarousel.prototype.build = function () {
    var viewport = this.viewport;
    var pageWidth = viewport.clientWidth;
    var scrollable = viewport.scrollWidth - pageWidth;

    /* A 2px slack keeps sub-pixel rounding from inventing an extra, unreachable page */
    this.pageCount = scrollable > 2 ? Math.ceil(viewport.scrollWidth / pageWidth) : 1;

    this.container.classList.toggle('lz-carousel--scrollable', scrollable > 2);

    if (this.dotsWrapper) {
      this.renderDots();
    }

    this.update();
  };

  LzCarousel.prototype.renderDots = function () {
    var wrapper = this.dotsWrapper;

    /* Nothing to page through, or the controls are hidden on this breakpoint */
    if (this.pageCount < 2) {
      wrapper.innerHTML = '';
      return;
    }

    if (wrapper.children.length === this.pageCount) return;

    wrapper.innerHTML = '';

    for (var i = 0; i < this.pageCount; i++) {
      var dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'lz-carousel__dot';
      dot.setAttribute('aria-label', String(i + 1));
      dot.dataset.lzCarouselPage = String(i);
      dot.addEventListener('click', this.goToPage.bind(this, i));
      wrapper.appendChild(dot);
    }
  };

  LzCarousel.prototype.goToPage = function (index) {
    this.viewport.scrollTo({
      left: this.viewport.clientWidth * index,
      behavior: 'smooth'
    });
  };

  LzCarousel.prototype.scrollByPage = function (direction) {
    this.viewport.scrollBy({
      left: this.viewport.clientWidth * direction,
      behavior: 'smooth'
    });
  };

  LzCarousel.prototype.update = function () {
    var viewport = this.viewport;
    var pageWidth = viewport.clientWidth;
    var maxScroll = viewport.scrollWidth - pageWidth;

    if (pageWidth > 0) {
      this.currentPage = Math.round(viewport.scrollLeft / pageWidth);
    }

    if (this.dotsWrapper) {
      var dots = this.dotsWrapper.children;
      for (var i = 0; i < dots.length; i++) {
        var isCurrent = i === this.currentPage;
        dots[i].classList.toggle('is-active', isCurrent);
        dots[i].setAttribute('aria-current', isCurrent ? 'true' : 'false');
      }
    }

    /*
      The arrows are dimmed rather than removed at the ends, so the row does not shift sideways
      as the visitor reaches the first or last card.
    */
    if (this.prev) {
      this.prev.disabled = viewport.scrollLeft <= 2;
    }
    if (this.next) {
      this.next.disabled = viewport.scrollLeft >= maxScroll - 2;
    }
  };

  LzCarousel.prototype.destroy = function () {
    this.viewport.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onResize);
    if (this.observer) this.observer.disconnect();
  };

  var instances = [];

  function initAll(scope) {
    var root = scope || document;
    var nodes = root.querySelectorAll('[data-lz-carousel]');

    Array.prototype.forEach.call(nodes, function (node) {
      if (node.dataset.lzCarouselReady === 'true') return;
      node.dataset.lzCarouselReady = 'true';
      instances.push(new LzCarousel(node));
    });
  }

  function ready(callback) {
    if (document.readyState !== 'loading') {
      callback();
    } else {
      document.addEventListener('DOMContentLoaded', callback);
    }
  }

  ready(function () {
    initAll();
  });

  /* Theme editor: a re-rendered section arrives with the ready flag stripped, so this re-binds */
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
}());

/*
  ============================================================================================
  Lazuela sections ported from the old theme.

  Everything below drives markup that Impulse itself knows nothing about, so it is kept in this
  file rather than in theme.js: the vendor bundle stays replaceable by a theme update.

  Contents
    A. Testimonials carousel (sections/lz-testimonials.liquid)
    B. Video lightbox tiles - paging is handled by LzCarousel above
    C. Bundle option cards - adds to cart (product block)
    D. Upsell toggle (product block)
    E. Offer bar countdown + copy code (product block)
  ============================================================================================
*/

/* ------------------------------------------------------------------------------------------
   A. Testimonials carousel

   Uses theme.Slideshow, the same Flickity wrapper every other carousel in Impulse goes through,
   so the dots, the drag behaviour and the arrows all match the rest of the site. theme.js is
   loaded before this file and defines theme.Slideshow while it parses, so it is already there
   by the time this runs; the guard is only for the case where theme.js failed to load at all.
   ------------------------------------------------------------------------------------------ */
(function () {
  'use strict';

  var instances = {};

  function init(el) {
    if (!window.theme || typeof theme.Slideshow !== 'function' || !window.Flickity) return;
    if (el.getAttribute('data-lz-testimonials-ready')) return;

    var count = parseInt(el.getAttribute('data-count'), 10) || 0;
    if (count < 1) return;

    var perRow = parseInt(el.getAttribute('data-per-row'), 10) || 3;
    var autoplay = parseInt(el.getAttribute('data-autoplay'), 10) || 0;

    el.setAttribute('data-lz-testimonials-ready', '1');

    var args = {
      adaptiveHeight: true,
      cellAlign: 'left',
      contain: true,
      dragThreshold: 16,
      /* One dot per card, the way the section behaved in the old theme */
      pageDots: count > 1,
      prevNextButtons: count > perRow,
      /* Wrapping a row that already shows everything just makes it jump for no reason */
      wrapAround: count > perRow,
      autoPlay: autoplay > 0 ? autoplay : false,
      pauseAutoPlayOnHover: true
    };

    var section = el.closest('[data-section-id]');
    var id = section ? section.getAttribute('data-section-id') : el.id;

    instances[id] = new theme.Slideshow(el, args);
  }

  function scan(scope) {
    var nodes = (scope || document).querySelectorAll('[data-lz-testimonials]');
    Array.prototype.forEach.call(nodes, init);
  }

  function ready(callback) {
    if (document.readyState !== 'loading') { callback(); }
    else { document.addEventListener('DOMContentLoaded', callback); }
  }

  ready(function () { scan(); });

  document.addEventListener('shopify:section:load', function (event) {
    scan(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var id = event.detail.sectionId;
    if (instances[id] && typeof instances[id].destroy === 'function') {
      instances[id].destroy();
      delete instances[id];
    }
  });

  /* Selecting a testimonial in the theme editor brings its card into view */
  document.addEventListener('shopify:block:select', function (event) {
    var slide = event.target.closest ? event.target.closest('.lz-testimonials__cell') : null;
    if (!slide) return;

    var section = slide.closest('[data-section-id]');
    var id = section ? section.getAttribute('data-section-id') : null;
    var index = parseInt(slide.getAttribute('data-index'), 10);

    if (id && instances[id] && typeof instances[id].pause === 'function' && !isNaN(index)) {
      instances[id].goToSlide(index);
      instances[id].pause();
    }
  });

  document.addEventListener('shopify:block:deselect', function (event) {
    var section = event.target.closest ? event.target.closest('[data-section-id]') : null;
    var id = section ? section.getAttribute('data-section-id') : null;

    if (id && instances[id] && typeof instances[id].play === 'function') {
      instances[id].play();
    }
  });
}());

/* ------------------------------------------------------------------------------------------
   C. Bundle option cards

   Clicking a card adds to the cart and does nothing else. Which variant goes in comes from the
   card itself when the merchant picked a product for it; when they did not, the id is read out
   of the product form at click time, so the card follows whatever size or colour the visitor
   currently has selected rather than a variant frozen in at render.

   Afterwards the theme's own `ajaxProduct:added` event is fired. That is what the cart drawer
   listens for, so a bundle click gives exactly the feedback an ordinary add-to-cart gives; with
   the cart set to a page instead of a drawer there is nothing listening, so the visitor is sent
   to the cart the way the theme would send them.
   ------------------------------------------------------------------------------------------ */
(function () {
  'use strict';

  function currentVariantId(card) {
    var scope = card.closest('.product-single__meta') || card.closest('[data-section-id]');
    if (!scope) return null;

    /* Impulse keeps the selected variant on this <select>, and updates it on every change */
    var select = scope.querySelector('[data-product-select]') || scope.querySelector('[name="id"]');
    return select ? select.value : null;
  }

  function finish(card, label) {
    card.removeAttribute('data-busy');
    card.disabled = false;
    if (label !== null && label !== undefined) card.setAttribute('aria-label', label);
  }

  document.addEventListener('click', function (event) {
    var card = event.target.closest ? event.target.closest('[data-lz-bundle-option]') : null;
    if (!card || card.hasAttribute('data-busy')) return;

    event.preventDefault();

    var group = card.closest('.lz-bundle__options');
    if (group) {
      var cards = group.querySelectorAll('[data-lz-bundle-option]');
      for (var i = 0; i < cards.length; i++) {
        cards[i].classList.remove('is-selected');
        cards[i].setAttribute('aria-pressed', 'false');
      }
    }
    card.classList.add('is-selected');
    card.setAttribute('aria-pressed', 'true');

    var variantId = card.getAttribute('data-lz-bundle-variant') || currentVariantId(card);
    if (!variantId) return;

    var quantity = parseInt(card.getAttribute('data-lz-bundle-quantity'), 10);
    if (!quantity || quantity < 1) quantity = 1;

    /* Locked while in flight, so an impatient double tap cannot add the pack twice */
    card.setAttribute('data-busy', '1');

    fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ id: variantId, quantity: quantity })
    })
      .then(function (response) {
        if (!response.ok) throw new Error('cart');
        return response.json();
      })
      .then(function (item) {
        var drawer = window.theme && theme.settings && theme.settings.cartType === 'drawer';

        if (drawer) {
          document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
            detail: { product: item, addToCartBtn: card }
          }));
          finish(card);
        } else {
          /* Nothing on the page shows the cart, so the visitor is taken to it */
          window.location.href = (window.theme && theme.routes && theme.routes.cartPage) || '/cart';
        }
      })
      .catch(function () {
        finish(card);
      });
  });
}());

/* ------------------------------------------------------------------------------------------
   D. Upsell toggle

   The switch reflects what is in the cart rather than what has been clicked, so a reload never
   leaves it stale. `cart:build` is Impulse's own way of asking the drawer to re-read the cart;
   it refreshes the count without forcing the drawer open on every toggle.
   ------------------------------------------------------------------------------------------ */
(function () {
  'use strict';

  function post(url, body) {
    return fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body)
    });
  }

  function refreshCart() {
    document.dispatchEvent(new CustomEvent('cart:build'));
    document.dispatchEvent(new CustomEvent('cart:updated'));
  }

  function sync() {
    var toggles = document.querySelectorAll('[data-lz-upsell-toggle]');
    if (!toggles.length) return;

    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (response) { return response.json(); })
      .then(function (cart) {
        for (var i = 0; i < toggles.length; i++) {
          var id = toggles[i].getAttribute('data-variant-id');
          var inCart = false;

          for (var j = 0; j < cart.items.length; j++) {
            if (String(cart.items[j].variant_id) === String(id)) { inCart = true; break; }
          }

          toggles[i].setAttribute('aria-checked', inCart ? 'true' : 'false');
        }
      })
      .catch(function () {});
  }

  document.addEventListener('click', function (event) {
    var toggle = event.target.closest ? event.target.closest('[data-lz-upsell-toggle]') : null;
    if (!toggle || toggle.disabled || toggle.hasAttribute('data-busy')) return;

    var id = toggle.getAttribute('data-variant-id');
    var on = toggle.getAttribute('aria-checked') === 'true';

    /* Locked while in flight, so a double tap cannot add the product twice */
    toggle.setAttribute('data-busy', '1');

    var request = on
      ? post('/cart/change.js', { id: id, quantity: 0 })
      : post('/cart/add.js', { id: id, quantity: 1 });

    request
      .then(function (response) {
        if (!response.ok) throw new Error('cart');
        toggle.setAttribute('aria-checked', on ? 'false' : 'true');
        refreshCart();
      })
      .catch(function () {})
      .then(function () { toggle.removeAttribute('data-busy'); });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', sync);
  } else {
    sync();
  }

  document.addEventListener('ajaxProduct:added', function () { window.setTimeout(sync, 400); });
  document.addEventListener('shopify:section:load', function () { window.setTimeout(sync, 100); });
}());

/* ------------------------------------------------------------------------------------------
   E. Offer bar: countdown and copy-to-clipboard

   The tick re-reads the DOM every second rather than caching the bars, so a block added in the
   theme editor starts counting without a reload.
   ------------------------------------------------------------------------------------------ */
(function () {
  'use strict';

  function pad(n) { return n < 10 ? '0' + n : String(n); }

  function tick() {
    var bars = document.querySelectorAll('[data-lz-offer-countdown]');

    for (var i = 0; i < bars.length; i++) {
      var el = bars[i];
      var raw = (el.getAttribute('data-end') || '').trim();
      if (!raw) continue;

      /* Safari refuses "YYYY-MM-DD HH:MM", so the separators are normalised first */
      var end = new Date(raw.replace(/-/g, '/')).getTime();
      if (isNaN(end)) continue;

      var left = Math.max(0, end - Date.now());
      var secs = Math.floor(left / 1000);
      var parts = {
        days: Math.floor(secs / 86400),
        hours: Math.floor((secs % 86400) / 3600),
        minutes: Math.floor((secs % 3600) / 60),
        seconds: secs % 60
      };

      for (var key in parts) {
        var node = el.querySelector('[data-unit="' + key + '"]');
        if (node) node.textContent = pad(parts[key]);
      }
    }
  }

  document.addEventListener('click', function (event) {
    var button = event.target.closest ? event.target.closest('[data-lz-offer-copy]') : null;
    if (!button) return;

    var code = button.getAttribute('data-code') || '';
    var done = button.getAttribute('data-copied-label') || 'OK';
    var original = button.textContent;

    function feedback() {
      button.textContent = done;
      setTimeout(function () { button.textContent = original; }, 2000);
    }

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(code).then(feedback, function () {});
    } else {
      var tmp = document.createElement('textarea');
      tmp.value = code;
      document.body.appendChild(tmp);
      tmp.select();
      try { document.execCommand('copy'); feedback(); } catch (e) {}
      document.body.removeChild(tmp);
    }
  });

  tick();
  setInterval(tick, 1000);
}());

/*
  ============================================================================================
  Cart drawer: the total in dollars, under the subtotal

  The short version
  -----------------
  The shop's own currency is USD. Bucks does not change that - it rewrites the price text in the
  browser, leaving Shopify's cart in dollars - so the figure this line needs is not a conversion
  at all. It is the cart total exactly as Shopify already holds it, printed somewhere Bucks will
  not paint over. There is no rate, no lookup and no geolocation involved: the visitor's country
  decides which currency they *read*, and Shopify has already used it before this file runs.

  Reading the total
  -----------------
  Two sources, in order. Impulse injects `.cart__items[data-cart-subtotal]` into the drawer on
  every cart change, which is the live figure. Before that first injection lands, the block's own
  `data-cart-total` - rendered by Liquid with the page - covers the gap. Both are in cents of the
  shop currency; the division by `Shopify.currency.rate` is there only so the line survives the
  day Shopify Markets is switched on and those cents stop being dollars.

  Deciding whether to show it
  ---------------------------
  This is what the two previous versions got wrong, so it is now built to fail the other way.
  The only reason to stay quiet is that the shopper is *already* reading dollars, and the honest
  test for that is the subtotal printed right above: whatever put it there - Bucks, Shopify, or
  nothing at all - if that text still carries the shop's currency code then dollars are what is
  on screen. Anything else, including every case where the check cannot be made at all, shows the
  line. A redundant line is a small cost; an invisible one is the bug that was reported twice.

  Diagnosing it
  -------------
  Adding `?lzfx=debug` to any storefront URL prints the raw readings in place of the figure, so a
  drawer that still looks wrong can be reported with a screenshot instead of a console session.
  ============================================================================================
*/
(function () {
  'use strict';

  var block = null;
  var valueEl = null;
  var shopCurrency = 'USD';
  var debug = false;

  function subtotalNode() {
    return document.querySelector('#CartDrawer [data-subtotal]');
  }

  /* The cart total in cents of the shop currency, live if Impulse has built the drawer yet */
  function totalCents() {
    var items = document.querySelector('#CartDrawer .cart__items[data-cart-subtotal]');
    var raw = items ? items.getAttribute('data-cart-subtotal') : block.getAttribute('data-cart-total');
    var cents = parseInt(raw, 10);

    return isNaN(cents) ? null : cents;
  }

  /* 1 unless Shopify itself is presenting another currency, which it is not while Bucks is used */
  function presentmentRate() {
    var rate = window.Shopify && Shopify.currency ? parseFloat(Shopify.currency.rate) : NaN;
    return rate > 0 ? rate : 1;
  }

  /*
    Is the subtotal on screen already in the shop's currency? Read from the text the shopper can
    actually see, because that is the only thing that answers the question regardless of which
    app - or none - produced it. The money format carries the code ("$ 91,60 USD", "R$ 472,03
    BRL"), which is what makes this readable. No subtotal, or no code in it, counts as "not
    dollars": the line shows, and at worst repeats the number above it.
  */
  function showingShopCurrency() {
    var node = subtotalNode();
    if (!node) return false;

    var text = node.textContent || '';
    if (text.indexOf(shopCurrency) !== -1) return true;

    /*
      A last resort for a money format with no currency code in it. Shopify resolves the country
      from the visitor's IP before the page is built, so this needs no lookup of its own; it only
      settles the handful of countries that spend dollars, and only when the text said nothing.
      */
    if (!/[A-Z]{3}/.test(text) && shopCurrency === 'USD') {
      var country = window.Shopify && Shopify.country ? String(Shopify.country).toUpperCase() : '';
      return country === 'US' || country === 'EC' || country === 'PA' || country === 'SV';
    }

    return false;
  }

  /*
    Formatted in the language the page is in, so a Spanish visitor reads "91,60 US$" and an
    English one "$91.60". The fallback is for the browsers that reject a locale tag.
  */
  function formatUsd(amount) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: 'USD'
      }).format(amount);
    } catch (e) {
      return 'US$ ' + amount.toFixed(2);
    }
  }

  /* Written only when it changes: Bucks re-converts often, and every pass wakes the observer */
  function write(text) {
    if (valueEl.textContent !== text) valueEl.textContent = text;
    block.classList.toggle('is-ready', text !== '');
  }

  function update() {
    if (!block) return;

    var cents = totalCents();
    var node = subtotalNode();

    if (debug) {
      write([
        'shop=' + shopCurrency,
        'cents=' + cents,
        'rate=' + presentmentRate(),
        'sub=' + (node ? JSON.stringify((node.textContent || '').trim()) : 'none'),
        'country=' + (window.Shopify && Shopify.country ? Shopify.country : '?'),
        'hide=' + showingShopCurrency()
      ].join(' | '));
      return;
    }

    if (cents === null || cents <= 0 || showingShopCurrency()) {
      write('');
      return;
    }

    write(formatUsd(cents / 100 / presentmentRate()));
  }

  var timer;
  function scheduleUpdate() {
    clearTimeout(timer);
    timer = setTimeout(update, 120);
  }

  function init() {
    block = document.querySelector('[data-lz-cart-fx]');
    if (!block) return;

    valueEl = block.querySelector('[data-lz-cart-fx-value]');
    if (!valueEl) return;

    shopCurrency = (block.getAttribute('data-shop-currency') || 'USD').toUpperCase();
    debug = window.location.search.indexOf('lzfx=debug') !== -1;

    if (debug) block.classList.add('lz-cart-fx--debug');

    if (window.MutationObserver) {
      /*
        Impulse replaces the contents of `[data-products]` on every cart change but does not
        announce it, so the rebuild itself is the signal. Watching for it covers the first fill
        on page load, quantity changes and adds from anywhere on the site.
      */
      var products = document.querySelector('#CartDrawer [data-products]');
      if (products) {
        new MutationObserver(scheduleUpdate).observe(products, { childList: true });
      }

      /*
        Bucks converts the subtotal a moment *after* Impulse writes it, and again whenever it
        re-runs. Until it does, the subtotal still reads in dollars and the line correctly stays
        hidden - so the conversion landing is itself the cue to look again.
      */
      var subtotal = subtotalNode();
      if (subtotal) {
        new MutationObserver(scheduleUpdate).observe(subtotal, {
          childList: true,
          subtree: true,
          characterData: true,
          attributes: true
        });
      }
    }

    document.addEventListener('cart:updated', scheduleUpdate);
    document.addEventListener('drawerOpen.CartDrawer', scheduleUpdate);

    /*
      A late safety net for the first paint. Bucks loads from its own app extension, so on a slow
      connection it can reach the subtotal after every event above has already fired, and on a
      browser without MutationObserver there is nothing else left watching.
    */
    window.setTimeout(update, 1200);
    window.setTimeout(update, 3000);

    update();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
}());

/*
  ============================================================================================
  The currency notice popup

  One dialog, opened two ways. The link under the dollar total opens it to be read, and starting
  checkout opens it as a last word before the payment page. All that differs is the button at the
  bottom, so the markup carries both labels and this swaps between them.

  The cart drawer stays open underneath the whole time. That is the point: closing the popup, by
  the X or by either button, should feel like stepping back into the cart rather than landing
  somewhere new. Nothing here closes the drawer, and Impulse's own `modalOpen` event - which every
  drawer listens for and closes itself on - is deliberately never fired.

  Holding back the checkout
  -------------------------
  The submit is caught while it is still travelling *down* to the form, before Impulse's own
  handler runs, so the button never picks up its loading spinner for a journey that is not
  happening yet. The one thing Impulse would have done in that handler is enforce the terms
  checkbox, so that check is repeated here first: an unticked box is left alone to reach Impulse
  and be refused, exactly as it would have been.

  Carrying on afterwards clicks the real button again with the guard lifted, rather than calling
  `form.submit()`. A form submitted in code carries no submitter, and it is the button's own
  `name="checkout"` that tells Shopify this is a checkout rather than a cart update.
  ============================================================================================
*/
(function () {
  'use strict';

  var notice = null;
  var dialog = null;
  var confirmButton = null;
  var form = null;
  var mode = 'read';
  var opener = null;
  var passThrough = false;
  var documentBound = false;

  var FOCUS_NAMESPACE = 'lz_notice_focus';

  function isOpen() {
    return notice && !notice.hasAttribute('hidden');
  }

  function drawerIsOpen() {
    return document.documentElement.classList.contains('js-drawer-open');
  }

  function open(nextMode) {
    if (!notice || isOpen()) return;

    mode = nextMode;
    opener = document.activeElement;

    var label = confirmButton.getAttribute(
      mode === 'checkout' ? 'data-label-checkout' : 'data-label-read'
    );
    if (label) confirmButton.textContent = label;
    confirmButton.disabled = false;

    notice.removeAttribute('hidden');

    /*
      The drawer holds the keyboard inside itself while it is open. It has to let go for as long
      as the popup is on top, or tabbing would walk around behind the dialog.
    */
    if (window.theme && theme.a11y) {
      theme.a11y.removeTrapFocus({ container: document.getElementById('CartDrawer'), namespace: 'drawer_focus' });
      theme.a11y.trapFocus({ container: dialog, namespace: FOCUS_NAMESPACE, elementToFocus: confirmButton });
    } else {
      confirmButton.focus();
    }
  }

  function close() {
    if (!isOpen()) return;

    notice.setAttribute('hidden', '');

    if (window.theme && theme.a11y) {
      theme.a11y.removeTrapFocus({ container: dialog, namespace: FOCUS_NAMESPACE });
    }

    /*
      Back to the cart, which is the only place either exit leads. It is normally still open
      behind the popup and needs nothing done to it; the reopen is for the case where something
      else closed it while the popup was up.
    */
    if (!drawerIsOpen()) {
      document.dispatchEvent(new CustomEvent('cart:open'));
    } else if (window.theme && theme.a11y) {
      theme.a11y.trapFocus({
        container: document.getElementById('CartDrawer'),
        namespace: 'drawer_focus',
        elementToFocus: opener && opener.offsetParent !== null ? opener : undefined
      });
    } else if (opener && opener.offsetParent !== null) {
      opener.focus();
    }

    opener = null;
  }

  function proceedToCheckout() {
    var button = form ? form.querySelector('.cart__checkout') : null;

    confirmButton.disabled = true;

    if (!button) {
      if (form) form.submit();
      return;
    }

    passThrough = true;
    button.click();
    passThrough = false;
  }

  function onConfirm() {
    if (mode === 'checkout') {
      proceedToCheckout();
      return;
    }
    close();
  }

  function init() {
    notice = document.querySelector('[data-lz-notice]');
    if (!notice) return;

    dialog = notice.querySelector('[data-lz-notice-dialog]');
    confirmButton = notice.querySelector('[data-lz-notice-confirm]');
    if (!dialog || !confirmButton) return;

    form = document.getElementById('CartDrawerForm');

    confirmButton.addEventListener('click', onConfirm);

    notice.addEventListener('click', function (event) {
      if (event.target.closest('[data-lz-notice-close]')) close();
    });

    /*
      The two handlers that live on the document rather than on markup are bound once and never
      again. Everything above is bound to nodes that the theme editor throws away and rebuilds
      with the header section, so re-running this picks up the new ones; these two would simply
      stack up, and the popup would start opening and closing twice per click.
    */
    if (!documentBound) {
      documentBound = true;

      document.addEventListener('click', function (event) {
        if (!event.target.closest) return;
        if (!event.target.closest('[data-lz-notice-open]')) return;

        event.preventDefault();
        open('read');
      });

      document.addEventListener('keydown', function (event) {
        if (event.key === 'Escape' && isOpen()) close();
      });
    }

    if (form && notice.getAttribute('data-on-checkout') !== 'false') {
      form.addEventListener('submit', function (event) {
        if (passThrough) return;

        /* An unticked terms box is Impulse's refusal to make, not ours */
        var terms = form.querySelector('.cart__terms-checkbox');
        if (terms && !terms.checked) return;

        event.preventDefault();
        event.stopPropagation();
        open('checkout');
      }, true);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* The header section carries this markup, so the editor rebuilding it has to be picked up */
  document.addEventListener('shopify:section:load', init);
}());
